import { PARTY_PLACES, partyColourOf, type ColourRoleName, type MapPlace, type MapPoint } from "odyssey-shared";
import { say } from "../../words.ts";
import type { PartyAway, PartyHere } from "../party/positions.ts";

/**
 * The world map's list of known places (ADR-100 decision 12 and the note of P2.12-T25): each place the character has
 * found, nearest first, each with its distance in tiles and its direction on the eight points of the compass, straight from
 * the character's feet ("Old Shrine, 42 tiles north-east"). Pure: positions in, entries out; the words are the
 * language files' (`worldMap.*`).
 */

/**
 * One of the eight points of the compass, named as the language files' `worldMap.direction` keys are.
 */
export type Direction = "north" | "northEast" | "east" | "southEast" | "south" | "southWest" | "west" | "northWest";

/**
 * The points in order round from east, clockwise on a page where y grows downward.
 */
const POINTS: readonly Direction[] = ["east", "southEast", "south", "southWest", "west", "northWest", "north", "northEast"];

/**
 * The kinds of place, in the order the panel's groups are shown. A later kind (a landmark) is added here and given its
 * heading word `worldMap.kind.<kind>`; nothing else changes.
 */
export const PLACE_KINDS = ["party", "shrine", "waypoint"] as const;

/**
 * A kind of place.
 */
export type PlaceKind = (typeof PLACE_KINDS)[number];

/**
 * A place the character knows, before any distance is taken from it: it moves only when the places do. A party member
 * (ADR-100 decision 10) is a place of kind `party` whose `place` is the roster place that gives its colour.
 */
export interface PlaceMark {
  readonly key: string;
  readonly kind: PlaceKind;
  readonly name: string;
  readonly handle: number;
  readonly x: number;
  readonly y: number;
  readonly place?: number;
}

/**
 * One place of the list.
 */
export interface LocatedPlace extends PlaceMark {
  readonly distance: number;
  readonly direction: Direction;
  readonly where?: "here";
}

/**
 * A party member who is on another map: named, with the roster place that gives their colour and no position, so no
 * distance, direction or pin can be read from it.
 */
export interface AwayPlace {
  readonly key: string;
  readonly kind: "party";
  readonly name: string;
  readonly handle: number;
  readonly place: number;
  readonly where: "away";
}

/**
 * One row of the list: a place with a position, or a party member away.
 */
export type KnownPlace = LocatedPlace | AwayPlace;

/**
 * The point of the compass a displacement lies toward, each point 45 degrees wide and centred on its own line, north up
 * the page (negative y).
 *
 * @param dx The displacement across, in tiles; positive is east.
 * @param dy The displacement down, in tiles; positive is south.
 * @returns The point.
 */
export function directionOf(dx: number, dy: number): Direction {
  const turns = Math.atan2(dy, dx) / (Math.PI / 4);
  const index = ((Math.round(turns) % 8) + 8) % 8;
  return POINTS[index] as Direction;
}

/**
 * Each found place as a mark, with its name: a waypoint (ADR-137 decision 2) is its own kind with its own mark and
 * heading, and an unnamed one reads "A waypoint", never "A shrine". The Spawn Point is not a place of the map (ADR-100's
 * note of P2.12-T25).
 *
 * @param places The places the character has found.
 * @returns The marks, in the order given.
 */
export function placeMarks(places: readonly MapPlace[]): readonly PlaceMark[] {
  return places.map((place) => {
    const waypoint = place.kind === "waypoint";
    const unnamed = waypoint ? say("worldMap.waypoint") : say("worldMap.shrine");
    return { key: `shrine-${place.handle}-${place.x}-${place.y}`, kind: waypoint ? "waypoint" : "shrine", name: place.name === "" ? unnamed : place.name, handle: place.handle, x: place.x, y: place.y };
  });
}

/**
 * The key of a party member's mark and row.
 *
 * @param place The member's roster place.
 * @returns The key.
 */
export function partyKey(place: number): string {
  return `party-${place}`;
}

/**
 * Each party member on this map as a mark (ADR-100 decision 10), with their first name.
 *
 * @param here The members on the map.
 * @returns The marks, in the order given.
 */
export function partyMarks(here: readonly PartyHere[]): readonly PlaceMark[] {
  return here.map((member) => ({ key: partyKey(member.place), kind: "party", name: member.name, handle: member.place, x: member.x, y: member.y, place: member.place }));
}

/**
 * Each party member who is on another map as a row of the list (ADR-100 decision 12): named, with no position, so
 * nothing in the list or on the map can pin it.
 *
 * @param away The members elsewhere.
 * @returns The entries, in the order given.
 */
export function awayEntries(away: readonly PartyAway[]): readonly AwayPlace[] {
  return away.map((member) => ({ key: partyKey(member.place), kind: "party", name: member.name, handle: member.place, place: member.place, where: "away" }));
}

const reported = new Set<string>();

/**
 * The colour role of a party member's mark or row. A party entry that lost its roster place has no colour to take, and
 * is not drawn rather than drawn in place 0's; the loss is logged once for each key.
 *
 * @param key The entry's key.
 * @param place The roster place it carries, if any.
 * @returns The colour role, or null when the entry has no valid place.
 */
export function partyColourFor(key: string, place: number | undefined): ColourRoleName | null {
  if (place !== undefined && Number.isInteger(place) && place >= 0 && place < PARTY_PLACES) {
    return partyColourOf(place);
  }
  if (!reported.has(key)) {
    reported.add(key);
    console.warn(`party entry ${key} has no roster place and is not drawn`);
  }
  return null;
}

/**
 * A mark with its distance and direction from a point.
 *
 * @param mark The mark.
 * @param from The character's feet in tiles.
 * @returns The place.
 */
export function measured(mark: PlaceMark, from: MapPoint): LocatedPlace {
  return { ...mark, distance: Math.round(Math.hypot(mark.x - from.x, mark.y - from.y)), direction: directionOf(mark.x - from.x, mark.y - from.y) };
}

/**
 * Orders marks nearest the character first. An equal distance puts the lower handle first, then the place further up
 * and further left.
 *
 * @param marks The marks.
 * @param from The character's feet in tiles.
 * @returns The entries, nearest first.
 */
export function nearestFirst(marks: readonly PlaceMark[], from: MapPoint): readonly LocatedPlace[] {
  return marks.map((mark) => measured(mark, from)).sort((a, b) => a.distance - b.distance || a.handle - b.handle || a.y - b.y || a.x - b.x);
}

/**
 * Builds the list: each found place, nearest the character first.
 *
 * @param places The places the character has found.
 * @param from The character's feet in tiles.
 * @returns The entries, nearest first.
 */
export function knownPlaces(places: readonly MapPlace[], from: MapPoint): readonly LocatedPlace[] {
  return nearestFirst(placeMarks(places), from);
}

/**
 * A distance and a direction in words: "42 tiles north-east".
 *
 * @param distance The distance in whole tiles.
 * @param direction The point of the compass.
 * @returns The words.
 */
export function distanceWords(distance: number, direction: Direction): string {
  return `${say("worldMap.tiles", { count: distance })} ${say(`worldMap.direction.${direction}`)}`;
}

/**
 * A place's line: its name, then its distance and direction, or "here" within half a tile.
 *
 * @param place The entry.
 * @returns The line, "Old Shrine, 42 tiles north-east".
 */
export function placeLine(place: KnownPlace): string {
  if (place.where === "away") {
    return say("worldMap.awayLine", { name: place.name, away: say("worldMap.elsewhere") });
  }
  if (place.distance === 0) {
    return say("worldMap.hereLine", { name: place.name, here: say("worldMap.here") });
  }
  return say("worldMap.line", { name: place.name, distance: say("worldMap.tiles", { count: place.distance }), direction: say(`worldMap.direction.${place.direction}`) });
}
